import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DO_APPLICATION_TYPE,
  BankingApplicationDocument,
  maskTaxId,
  STATE_OPTIONS,
  phoneFormatter,
  maskSSN,
  SigningState,
  AnalyticsEvents,
  AnalyticsPages,
} from 'client-experience-toolbox';
import { useApplication } from 'hooks/useApplication';
import baseStyles from './BaseFormStyles.scss';
import styles from './ReviewAndSubmit.scss';
import cx from 'classnames';
import { formatDate, sendDOFirebaseEvent } from 'utils/utils';
import { Button, DriftIcons } from 'drift-ui';
import { useDispatch } from 'react-redux';

export const ReviewAndSubmit: FC = () => {
  const { ArrowBack, CheckCircleIcon } = DriftIcons;
  const { application } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const applicationData = application?.applicationData as BankingApplicationDocument['applicationData'];
  const { t } = useTranslation();
  const [signingState, setSigningState] = useState(SigningState.Incomplete);
  const applicant = applicationData?.applicants?.[0];
  const dispatch = useDispatch();
  const [signingError, setSigningError] = useState(false);

  const company = applicationData?.company;
  const isSoleProprietor = company?.ownershipType === 'soleProprietor';
  const applicants = applicationData?.applicants || [];
  const visibleApplicants = isSoleProprietor ? applicants.slice(0, 1) : applicants;
  const controlProng = applicationData?.controlProng;

  const getBusinessTypeLabel = (ownershipType?: string) => {
    switch (ownershipType) {
      case 'soleProprietor':
        return t('digital_onboarding.banking.business_type.individual_sole_prop');
      case 'llc':
        return t('digital_onboarding.banking.business_type.llc');
      case 'partnership':
        return t('digital_onboarding.banking.business_type.partnership');
      case 'corporation':
        return t('digital_onboarding.banking.business_type.corporation');
      case 'noBusiness':
        return t('digital_onboarding.banking.business_type.no_business');
      default:
        return ownershipType;
    }
  };

  const getStateFormedLabel = (stateValue?: string) => {
    return STATE_OPTIONS.find((option) => option.value === stateValue)?.label || stateValue;
  };

  const controlProngApplicant = applicants.find(
    (applicant) => (applicant.key || applicant.applicantId) === controlProng?.key,
  );
  const baseName = controlProngApplicant ? `${controlProngApplicant.firstName} ${controlProngApplicant.lastName}` : '';
  const controlProngName = controlProngApplicant?.applicantId ? `You (${baseName})` : baseName;

  let displayedIcon;
  switch (signingState) {
    case SigningState.Incomplete:
      displayedIcon = <ArrowBack className={styles.displayIcon} htmlColor={'#ef3824'} />;
      break;
    case SigningState.Done:
      displayedIcon = <CheckCircleIcon className={styles.displayIcon} height={20} width={20} color="success" />;
      break;
  }

  const handleSignatureClick = () => {
    handleClickEvent('Primary Banking Applicant Signee field');
    setSigningState(SigningState.Done);
    if (signingError) {
      setSigningError(false);
    }
  };

  const handleClickEvent = (description: string) => {
    return sendDOFirebaseEvent(AnalyticsEvents.CLICK, AnalyticsPages.BANKING_APPLICATION_FORM, {
      description: description,
      applicationType: application?.type ?? 'unknown',
      applicationId: application?._id ?? 'unknown',
      user_email: applicant?.email ?? 'unknown',
    });
  };

  const handleSubmit = () => {
    if (signingState !== SigningState.Done) {
      setSigningError(true);
      return;
    }
    handleClickEvent('Primary Banking Applicant Submit');

    dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitting' });
  };

  return (
    <div className={cx(baseStyles.formContainer, styles.reviewContainer)}>
      <div className={styles.content}>
        <p className={styles.header}>{t('digital_onboarding.banking.review_and_sign.description')}</p>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('digital_onboarding.banking.banking_application_titles.business_background')}
          </h2>
          <div className={baseStyles.groupedFields}>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_background.operate_usd_question')}</p>
              <p className={styles.value}>{company?.operatesInUSD ? t('yes') : t('no')}</p>
            </div>
            <div className={styles.formQuestion}>
              <p className={styles.label}>
                {t('digital_onboarding.banking.business_background.company_operate_question')}
              </p>
              <p className={styles.value}>{company?.operatesInUS ? t('USA') : t('Somewhere else')}</p>
            </div>
          </div>
        </div>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('digital_onboarding.banking.banking_application_titles.business_history')}
          </h2>
          <div className={baseStyles.groupedFields}>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_history.state_formed_question')}</p>
              <p className={styles.value}>{getStateFormedLabel(company?.stateFormed)}</p>
            </div>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_history.year_formed_question')}</p>
              <p className={styles.value}>{company?.yearFormed}</p>
            </div>
          </div>
        </div>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('digital_onboarding.banking.banking_application_titles.business_type')}
          </h2>

          <div className={styles.formQuestion}>
            <p className={styles.label}>{t('digital_onboarding.banking.business_type.question_title')}</p>
            <p className={styles.value}>{getBusinessTypeLabel(company?.ownershipType)}</p>
          </div>
        </div>
        {!isSoleProprietor && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('digital_onboarding.banking.banking_application_titles.business_ownership')}
            </h2>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_ownership.ownership_question')}</p>
              <p className={styles.value}>{company?.hasMajorOwner ? t('yes') : t('no')}</p>
            </div>
          </div>
        )}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('digital_onboarding.banking.banking_application_titles.business_info')}
          </h2>
          <div className={baseStyles.groupedFields}>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_business_name')}</p>
              <p className={styles.value}>{company?.name}</p>
            </div>
            {company?.doingBusinessAs && (
              <div className={styles.formQuestion}>
                <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_doing_business_as')}</p>
                <p className={styles.value}>{company?.doingBusinessAs}</p>
              </div>
            )}
          </div>
          <div className={styles.formQuestion}>
            <p className={styles.label}>
              {t('digital_onboarding.banking.business_info.label_employer_identification_number')}
            </p>
            <p className={styles.value}>{maskTaxId(company?.irsTaxIdOrEinDisplay ?? '', true)}</p>
          </div>
          <div className={baseStyles.groupedFields}>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_street')}</p>
              <p className={styles.value}>{company?.address?.street}</p>
            </div>
            {company?.address?.secondaryStreet && (
              <div className={styles.formQuestion}>
                <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_secondary_street')}</p>
                <p className={styles.value}>{company?.address?.secondaryStreet}</p>
              </div>
            )}
          </div>
          {company?.address?.tertiaryStreet && (
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_tertiary_street')}</p>
              <p className={styles.value}>{company?.address?.tertiaryStreet}</p>
            </div>
          )}
          <div className={baseStyles.groupedFields}>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.common_section.label_city')}</p>
              <p className={styles.value}>{company?.address?.city}</p>
            </div>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.common_section.label_state')}</p>
              <p className={styles.value}>{company?.address?.state}</p>
            </div>
          </div>
          <div className={baseStyles.groupedFields}>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.common_section.label_zip')}</p>
              <p className={styles.value}>{company?.address?.zip}</p>
            </div>
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.common_section.label_country')}</p>
              <p className={styles.value}>USA</p>
            </div>
          </div>
          {company?.phone && (
            <div className={styles.formQuestion}>
              <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_business_phone')}</p>
              <p className={styles.value}>{phoneFormatter(company.phone, true)}</p>
            </div>
          )}
        </div>
        {visibleApplicants.length > 0 && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('digital_onboarding.owner_officer_section.description')}</h2>
            {visibleApplicants.map((applicant, index) => (
              <div key={applicant.applicantId || `applicant-${index}`} className={styles.ownerBlock}>
                {index > 0 && (
                  <h3 className={styles.sectionTitle}>
                    {t('digital_onboarding.banking.owner_info.additional_owner', { index: `#${index}` })}
                  </h3>
                )}
                {!isSoleProprietor && applicant.ownershipPercentage != null && (
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>
                      {t('digital_onboarding.owner_officer_section.label_ownership_percentage')}
                    </p>
                    <p className={styles.value}>{applicant.ownershipPercentage}%</p>
                  </div>
                )}
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.owner_officer_section.label_first_name')}</p>
                    <p className={styles.value}>{applicant.firstName}</p>
                  </div>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.owner_officer_section.label_last_name')}</p>
                    <p className={styles.value}>{applicant.lastName}</p>
                  </div>
                </div>
                <div className={styles.formQuestion}>
                  <p className={styles.label}>{t('digital_onboarding.banking.owner_info.label_ssn')}</p>
                  <p className={styles.value}>{maskSSN(applicant?.ssnOrItinDisplay ?? '', true)}</p>
                </div>
                <div className={styles.formQuestion}>
                  <p className={styles.label}>{t('digital_onboarding.owner_officer_section.label_date_of_birth')}</p>
                  <p className={styles.value}>{formatDate(applicant?.dateOfBirth ?? '')}</p>
                </div>
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_street')}</p>
                    <p className={styles.value}>{applicant.homeAddress?.street}</p>
                  </div>
                  {applicant.homeAddress?.secondaryStreet && (
                    <div className={styles.formQuestion}>
                      <p className={styles.label}>
                        {t('digital_onboarding.banking.business_info.label_secondary_street')}
                      </p>
                      <p className={styles.value}>{applicant.homeAddress.secondaryStreet}</p>
                    </div>
                  )}
                </div>
                {applicant.homeAddress?.tertiaryStreet && (
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>
                      {t('digital_onboarding.banking.business_info.label_tertiary_street')}
                    </p>
                    <p className={styles.value}>{applicant.homeAddress.tertiaryStreet}</p>
                  </div>
                )}
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_city')}</p>
                    <p className={styles.value}>{applicant.homeAddress?.city}</p>
                  </div>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_state')}</p>
                    <p className={styles.value}>{applicant.homeAddress?.state}</p>
                  </div>
                </div>
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_zip')}</p>
                    <p className={styles.value}>{applicant.homeAddress?.zip}</p>
                  </div>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_country')}</p>
                    <p className={styles.value}>USA</p>
                  </div>
                </div>
                <div className={baseStyles.groupedFields}>
                  {applicant.email && (
                    <div className={styles.formQuestion}>
                      <p className={styles.label}>{t('digital_onboarding.banking.owner_info.label_email')}</p>
                      <p className={styles.value}>{applicant.email}</p>
                    </div>
                  )}
                  {applicant.mobilePhoneNumber && (
                    <div className={styles.formQuestion}>
                      <p className={styles.label}>{t('digital_onboarding.common_section.label_personal_phone')}</p>
                      <p className={styles.value}>{phoneFormatter(applicant.mobilePhoneNumber, true)}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {!isSoleProprietor && controlProng && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('digital_onboarding.banking.banking_application_titles.control_prong')}
            </h2>

            {controlProngApplicant ? (
              <>
                <div className={styles.formQuestion}>
                  <p className={styles.label}>{t('digital_onboarding.banking.control_prong.title')}</p>
                  <p className={styles.value}>
                    {controlProngApplicant.firstName} {controlProngApplicant.lastName}
                  </p>
                </div>
                <div className={styles.formQuestion}>
                  <p className={styles.label}>
                    {t('digital_onboarding.banking.control_prong.job_title_question', {
                      control_prong: controlProngName,
                    })}
                  </p>
                  <p className={styles.value}>{controlProng?.jobTitle}</p>
                </div>
              </>
            ) : (
              <>
                <div className={styles.formQuestion}>
                  <p className={styles.label}>{t('digital_onboarding.banking.control_prong.title')}</p>
                  <p className={styles.value}>{t('digital_onboarding.banking.control_prong.someone_else_option')}</p>
                </div>
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.owner_officer_section.label_first_name')}</p>
                    <p className={styles.value}>{controlProng?.firstName}</p>
                  </div>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.owner_officer_section.label_last_name')}</p>
                    <p className={styles.value}>{controlProng?.lastName}</p>
                  </div>
                </div>
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.banking.owner_info.label_ssn')}</p>
                    <p className={styles.value}>
                      {controlProng?.ssnOrItinDisplay ? `XXX-XX-${controlProng.ssnOrItinDisplay}` : ''}
                    </p>
                  </div>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.owner_officer_section.label_date_of_birth')}</p>
                    <p className={styles.value}>{formatDate(controlProng?.dateOfBirth ?? '')}</p>
                  </div>
                </div>
                <div className={styles.formQuestion}>
                  <p className={styles.label}>{t('digital_onboarding.owner_officer_section.label_job_title')}</p>
                  <p className={styles.value}>{controlProng?.jobTitle}</p>
                </div>
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.banking.business_info.label_street')}</p>
                    <p className={styles.value}>{controlProng?.homeAddress?.street}</p>
                  </div>
                  {controlProng?.homeAddress?.secondaryStreet && (
                    <div className={styles.formQuestion}>
                      <p className={styles.label}>
                        {t('digital_onboarding.banking.business_info.label_secondary_street')}
                      </p>
                      <p className={styles.value}>{controlProng.homeAddress.secondaryStreet}</p>
                    </div>
                  )}
                </div>
                {controlProng?.homeAddress?.tertiaryStreet && (
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>
                      {t('digital_onboarding.banking.business_info.label_tertiary_street')}
                    </p>
                    <p className={styles.value}>{controlProng.homeAddress.tertiaryStreet}</p>
                  </div>
                )}
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_city')}</p>
                    <p className={styles.value}>{controlProng?.homeAddress?.city}</p>
                  </div>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_state')}</p>
                    <p className={styles.value}>{getStateFormedLabel(controlProng?.homeAddress?.state)}</p>
                  </div>
                </div>
                <div className={baseStyles.groupedFields}>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_zip')}</p>
                    <p className={styles.value}>{controlProng?.homeAddress?.zip}</p>
                  </div>
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_country')}</p>
                    <p className={styles.value}>USA</p>
                  </div>
                </div>
                {controlProng?.email && (
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.banking.owner_info.label_email')}</p>
                    <p className={styles.value}>{controlProng.email}</p>
                  </div>
                )}
                {controlProng?.mobilePhoneNumber && (
                  <div className={styles.formQuestion}>
                    <p className={styles.label}>{t('digital_onboarding.common_section.label_personal_phone')}</p>
                    <p className={styles.value}>{phoneFormatter(controlProng.mobilePhoneNumber, true)}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <div className={styles.signatureSection}>
          <p>{t('digital_onboarding.banking.review_and_sign.sign_below')}</p>
          <div className={styles.signatureContainer}>
            <button
              className={cx(
                styles.signatureButton,
                signingState === SigningState.Incomplete && styles.signatureContainerIncomplete,
              )}
              onClick={handleSignatureClick}
              disabled={signingState === SigningState.Done}
            >
              <div className={cx(styles.signatureTextBox, signingError && styles.signatureTextBoxError)}>
                <p className={cx(signingState === SigningState.Done && styles.deliusText, styles.signatureText)}>
                  {signingState === SigningState.Done
                    ? `${applicant?.firstName} ${applicant?.lastName}`
                    : t('digital_onboarding.banking.review_and_sign.click_to_sign')}
                </p>
                <div className={styles.signatureStatus}>
                  {displayedIcon}
                  {signingState !== SigningState.Done && (
                    <p
                      className={cx(
                        signingState === SigningState.Incomplete && styles.signatureButtonText,
                        styles.signatureText,
                      )}
                    >
                      {t('digital_onboarding.banking.review_and_sign.sign_here')}
                    </p>
                  )}
                </div>
              </div>
            </button>
            {signingError && <p className={styles.errorText}>{t('Required')}</p>}
          </div>
          <Button
            variant="contained"
            size="large"
            className={styles.submitButton}
            onClick={handleSubmit}
            color={'success'}
          >
            {t('digital_onboarding.banking.review_and_sign.submit_application')}
          </Button>
        </div>
      </div>
    </div>
  );
};
